import * as React from "react";
import { useContext, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { ApiClient } from "../../../apiUtils";
import {
  BatchJobStatusResponse,
  BatchJobTaskResponse,
} from "../../../api-client";

import { format } from "date-fns";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import { formatSeconds } from "../../../utils";

import {
  ColumnAutoSizeModule,
  ModuleRegistry,
  themeBalham,
  ColDef,
} from "ag-grid-community";
import { ClusterContext } from "../../../pages/Clusters/context";
import { CurrentSingleVmMetricsView } from "../../../pages/Clusters/Information/MetricsView";
import { Sheet, SheetContent } from "../../../components/ui/sheet";

ModuleRegistry.registerModules([ColumnAutoSizeModule]);

const TIME_FORMAT = "yyyy-MM-dd HH:mm:ss";

const getStateColor = (state: string) => {
  if (state.includes("error")) {
    return "text-red-500";
  }
  if (state === "running") {
    return "text-green-500";
  }
  if (state === "pending") {
    return "text-yellow-500";
  }
  return "";
};

const JobDetails = (props: { job: BatchJobStatusResponse }) => {
  const [vmDetail, setVmDetail] = useState<number | undefined>(undefined);

  const columns = useMemo(
    () =>
      [
        { field: "arrayTaskId", headerName: "Array ID", initialSort: "asc" },
        {
          field: "assignedTo.privateIpAddress",
          headerName: "Assigned To",
          onCellClicked: (e) => setVmDetail(e.data?.assignedToId),
        },
        {
          field: "state",
          cellRenderer: (v: any) => (
            <span className={getStateColor(v.value)}>{v.value}</span>
          ),
        },
        {
          field: "start",
          valueFormatter: (v) => (v.value ? format(v.value, TIME_FORMAT) : ""),
        },
        {
          field: "stop",
          valueFormatter: (v) => (v.value ? format(v.value, TIME_FORMAT) : ""),
        },
        {
          field: "duration",
          valueGetter: (v) =>
            v.data && v.data.stop && v.data.start
              ? // @ts-ignore
                formatSeconds((v.data.stop - v.data.start) / 1000, 1, "short")
              : "",
        },
        {
          field: "exitCode",
          cellRenderer: (v: any) => (
            <span className={v.value ? "text-red-500" : ""}>{v.value}</span>
          ),
        },
      ] as ColDef<BatchJobTaskResponse>[],
    [],
  );

  return props.job.tasks ? (
    <Box sx={{ padding: "1rem" }}>
      <Sheet
        open={!!vmDetail}
        onOpenChange={(open) => !open && setVmDetail(undefined)}
      >
        <SheetContent side="right" className="p-0">
          {!!vmDetail && <CurrentSingleVmMetricsView instanceId={vmDetail} />}
        </SheetContent>
      </Sheet>
      <AgGridReact
        domLayout="autoHeight"
        theme={themeBalham}
        autoSizeStrategy={{ type: "fitGridWidth" }}
        // @ts-ignore
        columnDefs={columns}
        rowData={props.job.tasks}
        getRowId={(param) => `${param.data.id}`}
      />
    </Box>
  ) : (
    <></>
  );
};

export const BatchJob = (props: { clusterId?: number }): React.ReactElement => {
  const { cluster } = useContext(ClusterContext);
  const { data, isLoading } = useQuery(
    ["fetchJobState", props.clusterId],
    () =>
      ApiClient.declarativeViewsJobGetClusterBatchJobView({
        clusterId: Number(props.clusterId),
      }),
    {
      refetchInterval: () => (cluster?.finishedAt === undefined ? 5000 : false),
      enabled: !!props.clusterId,
    },
  );

  return (
    <>
      {isLoading && !data ? (
        <>Loading...</>
      ) : (
        data?.map((job) => <JobDetails key={job.id} job={job} />)
      )}
    </>
  );
};
